import { Injectable, signal } from '@angular/core';
import {
  ACCESS_ROLES,
  AccessRole,
  APP_CONSTANTS,
  META_GROUPS,
  USER_ACTIVITY_EVENTS,
} from 'src/app/app.constants';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class AuthKenLiveService extends AuthService {
  // Storage keys
  private static readonly TOKEN_KEY = 'okta_access_token';
  private static readonly TOKEN_EXPIRY_KEY = 'okta_token_expiry';
  private static readonly USER_KEY = 'okta_user_name';
  private static readonly USER_DISPLAY_NAME = 'okta_user_display_name';
  private static readonly USER_GROUPS = 'okta_user_groups';
  private static readonly USER_PERMISSION = 'user_permission';
  private static readonly LOGIN_ATTEMPTS_KEY = 'okta_login_attempts';

  // Redirect-loop circuit breaker: Okta can bounce the browser through its own step-up/MFA
  // pages and land back here still unauthenticated (e.g. an abandoned step-up challenge). Left
  // unchecked, login() would immediately redirect to Okta again on every such landing, and Okta's
  // abuse detection eventually blocks the account for the resulting rapid-fire redirects.
  private static readonly MAX_LOGIN_ATTEMPTS = 3;
  private static readonly LOGIN_ATTEMPT_WINDOW_MS = 30000;

  // Logs the user out after this long with no user interaction, independent of (and typically
  // much shorter than) the token's own absolute expiry.
  private static readonly IDLE_TIMEOUT_MS = 60 * 60 * 1000;

  // Signals for user/session state
  private readonly authenticatedUser = signal<string | null>(null);
  private readonly authenticatedUserDisplayName = signal<string | null>(null);
  private readonly authenticated = signal(false);
  private readonly permission = signal<number | null>(null);
  private readonly isAccessLevelAdmin = signal(false);
  private readonly loginErrorSignal = signal<string | null>(null);

  // Timer that logs the user out the moment their token's expiry passes while the tab stays
  // open — otherwise expiry is only ever noticed lazily, the next time something happens to call
  // getAccessToken().
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;

  // Timer + listener bookkeeping for the idle-inactivity logout, kept separate from expiryTimer
  // above since the two run independently — whichever fires first wins.
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private idleListenersAttached = false;
  private readonly onUserActivity = (): void => this.resetIdleTimer();
  // Timestamp the idle timer will fire at, recomputed on every reset — lets callers (e.g. the
  // header countdown) read how much idle time is left without reaching into the timer itself.
  private idleDeadline: number | null = null;

  /**
   * Initializes the authentication service by handling Okta callback or restoring session.
   */
  public constructor() {
    super();
    const hash = window.location.hash;
    if (hash && hash.includes('error=')) {
      this.handleOktaError(hash);
    } else if (hash && hash.includes('access_token')) {
      this.handleOktaCallback(hash);
    } else {
      this.restoreSession();
    }
  }

  /**
   * Handles an Okta implicit-flow error callback (e.g. #error=access_denied&error_description=...).
   * Surfaces a friendly message instead of falling through to login(), which would otherwise
   * immediately retry the same failing flow.
   */
  private handleOktaError(hash: string): void {
    const params = new URLSearchParams(hash.substring(1));
    const description = params.get('error_description');
    this.loginErrorSignal.set(
      description
        ? decodeURIComponent(description.replace(/\+/g, ' '))
        : 'Sign-in was not completed.',
    );
    history.replaceState(null, '', window.location.pathname);
  }

  /**
   * Parses the Okta implicit-flow callback hash fragment and stores session data.
   * Expected format: #access_token=...&token_type=Bearer&expires_in=3600&...
   */
  private handleOktaCallback(hash: string): void {
    const params = new URLSearchParams(hash.substring(1));
    const accessToken = params.get('access_token');
    const expiresIn = Number(params.get('expires_in') ?? 3600);

    if (!accessToken) return;

    const expiryTime = Date.now() + expiresIn * 1000;
    const userName = this.extractUserName(accessToken);
    const displayName = this.extractUserDisplayName(accessToken);
    const groups = this.extractUserGroups(accessToken);
    localStorage.setItem(AuthKenLiveService.TOKEN_KEY, accessToken);
    localStorage.setItem(AuthKenLiveService.TOKEN_EXPIRY_KEY, String(expiryTime));
    if (userName) localStorage.setItem(AuthKenLiveService.USER_KEY, userName);
    if (displayName) localStorage.setItem(AuthKenLiveService.USER_DISPLAY_NAME, displayName);
    if (groups.length) localStorage.setItem(AuthKenLiveService.USER_GROUPS, JSON.stringify(groups));

    this.authenticatedUser.set(userName);
    this.authenticatedUserDisplayName.set(displayName);
    this.authenticated.set(true);
    this.scheduleExpiryLogout(expiryTime);
    this.startIdleTimeout();
    // A completed login means we're not stuck in a redirect loop — reset the circuit breaker.
    sessionStorage.removeItem(AuthKenLiveService.LOGIN_ATTEMPTS_KEY);

    // Restore the path the user was on before being redirected to Okta
    const returnPath = localStorage.getItem('localPath') || APP_CONSTANTS.DEFAULT_URL;
    localStorage.removeItem('localPath');
    history.replaceState(null, '', returnPath);
  }

  /**
   * Restores authentication state from localStorage on page reload.
   * Clears state silently if the token has expired.
   */
  private restoreSession(): void {
    const token = localStorage.getItem(AuthKenLiveService.TOKEN_KEY);
    const expiry = Number(localStorage.getItem(AuthKenLiveService.TOKEN_EXPIRY_KEY));

    if (token && expiry && Date.now() < expiry) {
      const userName = localStorage.getItem(AuthKenLiveService.USER_KEY);
      this.authenticatedUser.set(userName);
      this.authenticatedUserDisplayName.set(
        localStorage.getItem(AuthKenLiveService.USER_DISPLAY_NAME),
      );
      // Always re-extract groups from token (sets isAccessLevelAdmin as a side effect)
      this.extractUserGroups(token);
      // Only the restored user's own permission applies, and never the Super Admin sentinel.
      const stored = this.readStoredPermission();
      if (stored !== null && stored.user === userName && stored.permission >= 0) {
        this.permission.set(stored.permission);
      }
      this.authenticated.set(true);
      this.scheduleExpiryLogout(expiry);
      this.startIdleTimeout();
      // The token's groups claim can be stale (set at last login) - re-check against Okta so a
      // group change (e.g. losing the access-level admin group) takes effect on refresh instead
      // of only once the cached token expires. Best-effort: leaves the JWT-derived state above
      // untouched if Okta is unreachable or the org's authorization server doesn't expose a
      // 'groups' claim on this endpoint.
      void this.refreshGroupsFromOkta(token);
    } else if (token) {
      // Token exists but is expired — clear stale data
      this.clearStoredSession();
    }
  }

  /**
   * Starts (or restarts, on a restored session) the idle-inactivity logout: attaches the
   * activity listeners once, then arms the timer. Safe to call repeatedly — attaching listeners
   * twice is guarded against, and arming just resets the countdown.
   */
  private startIdleTimeout(): void {
    if (!this.idleListenersAttached) {
      for (const event of USER_ACTIVITY_EVENTS) {
        window.addEventListener(event, this.onUserActivity, { passive: true, capture: true });
      }
      this.idleListenersAttached = true;
    }
    this.resetIdleTimer();
  }

  /** Restarts the idle countdown from IDLE_TIMEOUT_MS — called on startup and on every activity
   *  event while a session is active. */
  private resetIdleTimer(): void {
    if (this.idleTimer !== null) clearTimeout(this.idleTimer);
    this.idleDeadline = Date.now() + AuthKenLiveService.IDLE_TIMEOUT_MS;
    this.idleTimer = setTimeout(() => this.logout(), AuthKenLiveService.IDLE_TIMEOUT_MS);
  }

  /** Cancels the idle timer and detaches its activity listeners. Called on logout so a stale
   *  timer can't fire against an already-cleared session, and so listeners don't pile up across
   *  repeated login/logout cycles in the same tab. */
  private stopIdleTimeout(): void {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    this.idleDeadline = null;
    if (this.idleListenersAttached) {
      for (const event of USER_ACTIVITY_EVENTS) {
        window.removeEventListener(event, this.onUserActivity, { capture: true });
      }
      this.idleListenersAttached = false;
    }
  }

  /**
   * (Re)schedules the timer that logs the user out the moment `expiryTime` passes. Called
   * whenever a token's expiry becomes known — on login and on restoring a session from
   * localStorage — since setTimeout doesn't survive a page reload on its own. Cancels any
   * previously scheduled timer first so a restored session doesn't end up with two pending.
   */
  private scheduleExpiryLogout(expiryTime: number): void {
    if (this.expiryTimer !== null) clearTimeout(this.expiryTimer);
    const delay = expiryTime - Date.now();
    if (delay <= 0) {
      this.logout();
      return;
    }
    this.expiryTimer = setTimeout(() => this.logout(), delay);
  }

  /**
   * Re-checks the current user's group membership directly against Okta's userinfo endpoint,
   * using the still-valid access token, and applies it in place of whatever was baked into the
   * token at last login. No-ops (keeping the JWT-derived state) if the request fails or the
   * response doesn't include a 'groups' claim.
   */
  private async refreshGroupsFromOkta(token: string): Promise<void> {
    try {
      const res = await fetch(`${environment.oktaOrigin}/oauth2/default/v1/userinfo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const body = await res.json();
      if (!Array.isArray(body.groups)) return;
      this.applyGroupClaims(body.groups);
    } catch {
      // Network/CORS failure — keep the JWT-derived state.
    }
  }

  /**
   * Decodes the JWT payload to extract a human-readable user name.
   * Claims are checked in order of preference: name → email → preferred_username → sub.
   */
  private extractUserName(token: string): string | null {
    try {
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64));
      return payload.name ?? payload.email ?? payload.preferred_username ?? payload.sub ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Decodes the JWT payload to extract a display name (firstName + lastName or sub).
   */
  private extractUserDisplayName(token: string): string | null {
    try {
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64));
      return payload.firstName && payload.lastName
        ? payload.firstName + ' ' + payload.lastName
        : (payload.sub ?? null);
    } catch {
      return null;
    }
  }

  /**
   * Decodes the JWT payload to extract user groups, filtering out meta groups.
   */
  private extractUserGroups(token: string): string[] {
    try {
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64));
      return this.applyGroupClaims(payload.groups);
    } catch {
      return [];
    }
  }

  /**
   * Sets the access-level-admin signal from a raw groups claim (from either a decoded JWT or an
   * Okta userinfo response) and returns the filtered, display-facing group list.
   */
  private applyGroupClaims(groups: string[] | undefined): string[] {
    const isAdmin = groups?.includes(META_GROUPS.accessLevelGroup + ' Admin') ?? false;
    this.isAccessLevelAdmin.set(isAdmin);
    return (
      groups?.filter(
        (e: string) =>
          !e.startsWith(META_GROUPS.accessLevelGroup) &&
          !e.startsWith(META_GROUPS.regionGroup) &&
          e !== META_GROUPS.everyone,
      ) ?? []
    );
  }

  /**
   * Clears all stored session data from localStorage.
   */
  private clearStoredSession(): void {
    localStorage.removeItem(AuthKenLiveService.TOKEN_KEY);
    localStorage.removeItem(AuthKenLiveService.TOKEN_EXPIRY_KEY);
    localStorage.removeItem(AuthKenLiveService.USER_KEY);
    localStorage.removeItem(AuthKenLiveService.USER_DISPLAY_NAME);
    localStorage.removeItem(AuthKenLiveService.USER_GROUPS);
    localStorage.removeItem(AuthKenLiveService.USER_PERMISSION);
  }

  /**
   * Returns the stored access token if it has not expired, otherwise null.
   */
  public override getAccessToken(): string | null {
    const token = localStorage.getItem(AuthKenLiveService.TOKEN_KEY);
    const expiry = Number(localStorage.getItem(AuthKenLiveService.TOKEN_EXPIRY_KEY));
    return token && expiry && Date.now() < expiry ? token : null;
  }

  /**
   * Returns the milliseconds remaining until the current token expires (and the user is
   * automatically logged out — see scheduleExpiryLogout), or null if there is no valid session.
   */
  public tokenExpiresInMs(): number | null {
    const expiry = Number(localStorage.getItem(AuthKenLiveService.TOKEN_EXPIRY_KEY));
    if (!expiry) return null;
    const remaining = expiry - Date.now();
    return remaining > 0 ? remaining : null;
  }

  /**
   * Returns the milliseconds remaining before inactivity logs the user out (see
   * startIdleTimeout/resetIdleTimer), or null if there is no active idle timer.
   */
  public override idleTimeoutInMs(): number | null {
    if (this.idleDeadline === null) return null;
    const remaining = this.idleDeadline - Date.now();
    return remaining > 0 ? remaining : null;
  }

  /**
   * Returns true when a non-expired token is in localStorage and the authenticated signal is set.
   */
  public override isAuthenticated(): boolean {
    return this.authenticated();
  }

  /**
   * Returns the best available display name for the authenticated user.
   * Prefers firstName + lastName, falls back to the email/username claim, then 'Unknown User'.
   */
  public override userName(): string {
    return this.authenticatedUserDisplayName() ?? this.authenticatedUser() ?? 'Unknown User';
  }

  /**
   * Returns a friendly message when the last sign-in attempt failed or was interrupted by a
   * redirect loop, so LoginKenLiveComponent can show it instead of silently retrying.
   */
  public loginError(): string | null {
    return this.loginErrorSignal();
  }

  /**
   * Saves the current path and redirects to the Okta authorization endpoint.
   * Refuses to redirect (and sets loginError instead) once too many attempts have happened in
   * quick succession — see MAX_LOGIN_ATTEMPTS.
   */
  public override login(): void {
    if (this.hasExceededLoginAttempts()) {
      this.loginErrorSignal.set(
        "We're having trouble signing you in. Please wait a moment and try again.",
      );
      return;
    }
    this.loginErrorSignal.set(null);
    location.replace(environment.oktaOrigin + environment.authOrigin);
  }

  /**
   * Clears the login error and attempt history, then retries. For the user-initiated "Try Again"
   * action — never called automatically, so it can't itself contribute to a redirect loop.
   */
  public retryLogin(): void {
    sessionStorage.removeItem(AuthKenLiveService.LOGIN_ATTEMPTS_KEY);
    this.loginErrorSignal.set(null);
    this.login();
  }

  /**
   * Records this login attempt and returns true once MAX_LOGIN_ATTEMPTS have occurred within
   * LOGIN_ATTEMPT_WINDOW_MS. Attempts are timestamped in sessionStorage so the count survives
   * the full-page navigations login() performs (a plain in-memory counter would not).
   */
  private hasExceededLoginAttempts(): boolean {
    const now = Date.now();
    let attempts: number[] = [];
    try {
      attempts = JSON.parse(sessionStorage.getItem(AuthKenLiveService.LOGIN_ATTEMPTS_KEY) ?? '[]');
    } catch {
      attempts = [];
    }
    attempts = attempts.filter((ts) => now - ts < AuthKenLiveService.LOGIN_ATTEMPT_WINDOW_MS);

    if (attempts.length >= AuthKenLiveService.MAX_LOGIN_ATTEMPTS) {
      return true;
    }

    attempts.push(now);
    sessionStorage.setItem(AuthKenLiveService.LOGIN_ATTEMPTS_KEY, JSON.stringify(attempts));
    return false;
  }

  /**
   * Clears local auth state, then logs out through Okta and redirects.
   *
   * Uses top-level navigation to /login/signout so the Okta session cookie is sent reliably,
   * including private/incognito cases where third-party cookie requests may be blocked.
   */
  public override logout(): void {
    if (this.expiryTimer !== null) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }
    this.stopIdleTimeout();
    this.clearStoredSession();
    this.authenticatedUser.set(null);
    this.authenticatedUserDisplayName.set(null);
    this.authenticated.set(false);
    const authorizeUrl = environment.oktaOrigin + environment.authOrigin;
    location.replace(
      `${environment.oktaOrigin}/login/signout?fromURI=${encodeURIComponent(authorizeUrl)}`,
    );
  }

  /**
   * Sets the user-scope permission for the current user and stores it. An unassigned permission
   * (the server left it unset) resolves to DEFAULT_KENLIVE_ACCESS_ROLE's value.
   */
  public override setPermission(permission: number | undefined): void {
    const resolved = this.permissionValue(permission);
    this.permission.set(resolved);
    localStorage.setItem(
      AuthKenLiveService.USER_PERMISSION,
      JSON.stringify({ user: this.authenticatedUser(), permission: resolved }),
    );
  }

  /** Reads the stored permission record, or null when it is absent or malformed. */
  private readStoredPermission(): { user: string | null; permission: number } | null {
    try {
      const raw = localStorage.getItem(AuthKenLiveService.USER_PERMISSION);
      if (raw === null) return null;
      const parsed = JSON.parse(raw);
      return Number.isInteger(parsed?.permission) ? parsed : null;
    } catch {
      return null;
    }
  }

  /**
   * Returns the access role for the current user, derived from their user-scope permission.
   */
  public override accessRight(): AccessRole {
    // The Okta access-level admin group confers Super Admin, which the server never sends.
    if (this.isAccessLevelAdmin()) return ACCESS_ROLES.SUPER_ADMIN;
    return this.roleForPermission(this.permission());
  }
}
